
import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DialogResult, DialogService, SpinnerService } from 'dspx-core';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { BaseCardSetListComponent } from '../base-card-set-list-component/base-card-set-list.component';
import { AppDataService } from 'src/app/services/app-data.service';
import { ResponseParserService } from 'src/app/services/response-parser.service';
import { TEMPLATES } from 'src/app/constants/templates.constants';
import { Router, ActivatedRoute } from '@angular/router';
import { API_ENDPOINTS } from 'src/app/constants/api-endpoints.constants';
import { VptasSpecialistGridFilterRequest } from 'src/app/interfaces/vptas-specialist-grid-filter-request.interface';
import { DashboardResponse } from 'src/app/interfaces/dashboard-response.interface';
import { take } from 'rxjs';

interface SpecialistProviderInformation {
  id?: string;
  code?: string;
  displayText?: string;
  manualAddress: boolean;
  addressAuto?: string;
  addressAutoStreetNumberName?: string;
  addressAutoSuburb?: string;
  addressAutoState?: string;
  addressAutoPostCode?: string;
  addressManualStreetNumberName?: string;
  addressManualSuburb?: string;
  addressManualState?: string;
  addressManualPostCode?: string;
}

interface SpecialistOption {
  code: string;
  text: string;
}

interface DropdownOption {
  label: string;
  value: string;
}

interface ProviderAddressOption {
  label: string;
  value: SpecialistProviderInformation;
}

@Component({
  selector: 'app-vptas-specialist-search-form',
  templateUrl: './vptas-specialist-search-form.component.html',
  styleUrl: './vptas-specialist-search-form.component.scss'
})
export class VptasSpecialistSearchFormComponent extends BaseCardSetListComponent implements OnInit, AfterViewInit {

  private readonly defaultStateValue = 'C80EE3AE-1E2E-4C2E-A540-871F9F0B4B64';

  filterForm: FormGroup;
  specialists: any[] = [];
  loading = false;
  searchPerformed = false;
  dialogResult: DialogResult;

  stateOptions: DropdownOption[] = [
    { label: 'All States', value: '' },
  ];

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected spinnerService: SpinnerService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected appDataService: AppDataService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private fb: FormBuilder
  ) {
    super(
      router,
      spinnerService,
      dialogService,
      responseParserService,
      appDataService,
      TEMPLATES.vptasSpecialistList,
      () => API_ENDPOINTS.dashboard.getVptasSpecialists,
      false
    );

    this.filterForm = this.fb.group({
      firstName: [''],
      middleName: [''],
      lastName: [''],
      providerNumber: [''],
      suburb: [''],
      specialties: [''],
      state: [this.defaultStateValue]
    });
  }

  ngOnInit(): void {
    this.loadStateOptions();
  }

  ngAfterViewInit(): void {}

  private loadStateOptions(): void {
    this.appDataService.getData(`${API_ENDPOINTS.referenceData.getOptionSetByCategory}/australianStates`, {})
      .pipe(take(1))
      .subscribe((data: any) => {
        const stateFilterOptions = this.extractOptionSetOptions(data);

        if (!Array.isArray(stateFilterOptions) || stateFilterOptions.length === 0) {
          return;
        }

        this.stateOptions = [
          { label: '', value: '' },
          ...stateFilterOptions
            .filter((option: any) => option != null)
            .map((option: any) => this.mapToDropdownOption(option))
            .filter((option: DropdownOption | null) => option !== null)
            .filter((option: DropdownOption) => option.label.length > 0 && option.value.length > 0)
        ];

        const defaultStateValueLower = this.defaultStateValue.toLowerCase();
        const matchedDefaultState = this.stateOptions.find((option) => option.value.toLowerCase() === defaultStateValueLower);
        const nonEmptyStateOptions = this.stateOptions.filter((option) => option.value.length > 0);
        const autoSelectedState = nonEmptyStateOptions.length === 1 ? nonEmptyStateOptions[0].value : null;
        this.filterForm.get('state')?.setValue(matchedDefaultState?.value ?? autoSelectedState ?? '');
      });
  }

  search(): void {
    const selectedStateValue = this.normalizeStateControlValue(this.filterForm.get('state')?.value);
    this.filterForm.get('state')?.setValue(selectedStateValue, { emitEvent: false });

    const filterRequest: VptasSpecialistGridFilterRequest = this.filterForm.value;
    const requestPayload: VptasSpecialistGridFilterRequest = {
      ...filterRequest,
      state: this.normalizeStateGuids(selectedStateValue),
      isDetailedSearch: true
    };
    this.loading = true;
    this.spinnerService.showSpinner();
    this.appDataService.post(API_ENDPOINTS.dashboard.getVptasSpecialists, requestPayload)
      .pipe(take(1))
      .subscribe((data: DashboardResponse) => {
        this.spinnerService.hideSpinner();
        this.loading = false;
        this.searchPerformed = true;
        const sectionModel = data?.response?.form?.tabs?.tab1?.page1?.specialistsSection;
        const specialists = sectionModel?.specialistsGrid ?? [];

        this.specialists = specialists.map(specialist => {
          const parsedSpecialties = this.parseDelimitedValues(specialist?.specialties, ',');
          const parsedProviderNumbers = this.parseDelimitedValues(specialist?.providerNumbers, ',');
          const parsedAddresses = this.parseDelimitedValues(specialist?.addresses, '|');
          const providerInformation: SpecialistProviderInformation[] = Array.isArray(specialist?.providersInformation)
            ? specialist.providersInformation
            : [];
          const backendSpecialtyOptions: SpecialistOption[] = Array.isArray(specialist?.specialtyOptions)
            ? specialist.specialtyOptions
            : [];

          const specialtyOptions = backendSpecialtyOptions.length > 0
            ? backendSpecialtyOptions.map((specialtyOption) => ({
              label: specialtyOption.text,
              value: specialtyOption.code
            }))
            : parsedSpecialties.map((specialty) => ({ label: specialty, value: specialty }));

          const providerAddressOptions: ProviderAddressOption[] = providerInformation.length > 0
            ? providerInformation.map((providerAddress) => ({
              label: providerAddress?.displayText || this.getProviderAddressDisplay(providerAddress),
              value: providerAddress
            }))
            : this.buildLegacyProviderAddressOptions(parsedProviderNumbers, parsedAddresses);

          return {
            ...specialist,
            specialtyOptions,
            providerAddressOptions,
            selectedSpecialty: specialtyOptions.length === 1 ? specialtyOptions[0].value : '',
            selectedProviderAddress: providerAddressOptions.length === 1 ? providerAddressOptions[0].value : null
          };
        });

        // Keep state dropdown bound to a single string GUID after search response.
        this.filterForm.get('state')?.setValue(selectedStateValue, { emitEvent: false });
      });
  }  

  clearFilters(): void {
    this.filterForm.reset({ firstName: '', middleName: '', lastName: '', providerNumber: '', suburb: '', specialties: '', state: '' });
    this.specialists = [];
    this.searchPerformed = false;
  }

  private normalizeStateControlValue(stateValue: string | string[] | null | undefined): string {
    if (Array.isArray(stateValue)) {
      const firstState = `${stateValue[0] || ''}`.trim();
      return firstState;
    }

    return `${stateValue || ''}`.trim();
  }

  private normalizeStateGuids(stateValue: string | string[] | null | undefined): string[] {
    if (Array.isArray(stateValue)) {
      return stateValue
        .map((state) => `${state}`.trim())
        .filter((state) => state.length > 0);
    }

    const normalizedState = `${stateValue || ''}`.trim();
    return normalizedState ? [normalizedState] : [];
  }

  private extractOptionSetOptions(data: any): any[] {
    if (Array.isArray(data)) {
      return data;
    }

    return [];
  }
  
  private mapToDropdownOption(option: any): DropdownOption | null {
    const label = `${option?.text || option?.label || option?.name || ''}`.trim();
    const value = `${option?.code || option?.value || option?.id || ''}`.trim();

    if (!label || !value) {
      return null;
    }

    return { label, value };
  }

  private createFallbackProviderInformation(addressAuto: string, providerNumber?: string): SpecialistProviderInformation {
    const normalizedAddress = `${addressAuto || ''}`.trim();
    const normalizedProviderNumber = `${providerNumber || ''}`.trim();
    const displayText = normalizedProviderNumber && normalizedAddress
      ? `${normalizedProviderNumber} - ${normalizedAddress}`
      : (normalizedProviderNumber || normalizedAddress);

    return {
      code: normalizedProviderNumber,
      displayText,
      manualAddress: false,
      addressAuto: normalizedAddress,
      addressAutoStreetNumberName: '',
      addressAutoSuburb: '',
      addressAutoState: '',
      addressAutoPostCode: ''
    };
  }

  private buildLegacyProviderAddressOptions(providerNumbers: string[], addresses: string[]): ProviderAddressOption[] {
    const maxLength = Math.max(providerNumbers.length, addresses.length);
    if (maxLength === 0) {
      return [];
    }

    return Array.from({ length: maxLength }).map((_, index) => {
      const providerNumber = providerNumbers[index] || providerNumbers[0] || '';
      const address = addresses[index] || addresses[0] || '';
      const providerInfo = this.createFallbackProviderInformation(address, providerNumber);
      return {
        label: providerInfo.displayText || this.getProviderAddressDisplay(providerInfo),
        value: providerInfo
      };
    });
  }

  private buildManualAddress(providerAddress: SpecialistProviderInformation): string {
    const manualParts = [
      providerAddress?.addressManualStreetNumberName,
      providerAddress?.addressManualSuburb,
      providerAddress?.addressManualState,
      providerAddress?.addressManualPostCode
    ].filter((value) => !!value && `${value}`.trim().length > 0);

    return manualParts.join(', ');
  }

  private getProviderAddressDisplay(providerAddress: SpecialistProviderInformation): string {
    return providerAddress?.addressAuto
      || this.buildManualAddress(providerAddress)
      || '';
  }

  private parseDelimitedValues(rawValue: any, separator: string): string[] {
    if (!rawValue) {
      return [];
    }

    if (Array.isArray(rawValue)) {
      return rawValue
        .map((value) => `${value}`.trim())
        .filter((value) => value.length > 0);
    }

    return `${rawValue}`
      .split(separator)
      .map((value) => value.trim())
      .filter((value) => value.length > 0);
  }

  private getSelectedProviderInformation(specialist: any): SpecialistProviderInformation | null {
    if (specialist?.selectedProviderAddress && typeof specialist.selectedProviderAddress === 'object') {
      return specialist.selectedProviderAddress as SpecialistProviderInformation;
    }

    // Backward compatibility for existing records still using old field names.
    if (specialist?.selectedAddress && typeof specialist.selectedAddress === 'object') {
      return specialist.selectedAddress as SpecialistProviderInformation;
    }

    if (typeof specialist?.selectedAddress === 'string' && specialist.selectedAddress.trim()) {
      return this.createFallbackProviderInformation(specialist.selectedAddress.trim());
    }

    if (Array.isArray(specialist?.providerAddressOptions) && specialist.providerAddressOptions.length === 1) {
      return specialist.providerAddressOptions[0]?.value as SpecialistProviderInformation;
    }

    if (Array.isArray(specialist?.providersInformation) && specialist.providersInformation.length === 1) {
      return specialist.providersInformation[0] as SpecialistProviderInformation;
    }

    return null;
  }

  private getSelectedSpecialtyLabel(specialist: any): string {
    const selected = specialist?.specialtyOptions?.find((option) => option?.value === specialist?.selectedSpecialty);
    return selected?.label || '';
  }

  selectSpecialist(specialist: any): void {
    const selectedProviderInformation = this.getSelectedProviderInformation(specialist);
    const isManualAddress = selectedProviderInformation?.manualAddress ?? false;
    const selectedProviderNumber = selectedProviderInformation?.code || specialist?.selectedProviderNumber || '';

    const autoAddress = selectedProviderInformation?.addressAuto || '';
    const autoStreetNumberName = selectedProviderInformation?.addressAutoStreetNumberName || '';
    const autoSuburb = selectedProviderInformation?.addressAutoSuburb || '';
    const autoState = selectedProviderInformation?.addressAutoState || '';
    const autoPostCode = selectedProviderInformation?.addressAutoPostCode || '';

    const manualStreetNumberName = selectedProviderInformation?.addressManualStreetNumberName || '';
    const manualSuburb = selectedProviderInformation?.addressManualSuburb || '';
    const manualState = selectedProviderInformation?.addressManualState || '';
    const manualPostCode = selectedProviderInformation?.addressManualPostCode || '';
    const manualAddress = this.buildManualAddress(selectedProviderInformation || { manualAddress: true });

    const selectedAddress = isManualAddress ? manualAddress : autoAddress;
    const selectedStreetNumberName = isManualAddress ? manualStreetNumberName : autoStreetNumberName;
    const selectedSuburb = isManualAddress ? manualSuburb : autoSuburb;
    const selectedState = isManualAddress ? manualState : autoState;
    const selectedPostCode = isManualAddress ? manualPostCode : autoPostCode;

    this.ref.close({
      result: DialogResult.ACCEPTED,
      specialistProviderID: specialist.id,
      specialistFirstName: specialist.firstName,
      specialistMiddleName: specialist.middleName,
      specialistName: specialist.lastName,
      specialistProviderNumber: selectedProviderNumber,
      specialistSpecialty: specialist.selectedSpecialty,
      specialistSpecialtyText: this.getSelectedSpecialtyLabel(specialist),
      specialistAddress: selectedAddress,
      specialistProviderInformation: selectedProviderInformation,
      specialistAddressManual: isManualAddress,
      specialistAddressStreetNumberName: selectedStreetNumberName,
      specialistAddressSuburb: selectedSuburb,
      specialistAddressState: selectedState,
      specialistAddressPostCode: selectedPostCode,
      specialistAddressAuto: autoAddress,
      specialistAddressAutoStreetNumberName: autoStreetNumberName,
      specialistAddressAutoSuburb: autoSuburb,
      specialistAddressAutoState: autoState,
      specialistAddressAutoPostCode: autoPostCode,
      specialistAddressManualStreetNumberName: manualStreetNumberName,
      specialistAddressManualSuburb: manualSuburb,
      specialistAddressManualState: manualState,
      specialistAddressManualPostCode: manualPostCode
    });
  }

  handleCancelClick(): void {
    this.ref.close({ result: DialogResult.REJECTED });
  }
}
